import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Check, BedDouble, ArrowUp } from 'lucide-react';

const FilterSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 850px) {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.8));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    padding: 24px;
    margin: 8px; /* Reduziert von 20px für mehr Breite */
    margin-bottom: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.3);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 20px;
      padding: 1px;
      background: linear-gradient(145deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.1));
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: xor;
      -webkit-mask-composite: xor;
      pointer-events: none;
    }
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  color: #ffffff;
  margin: 0 0 24px 0;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 850px) {
    font-size: 20px;
    font-weight: 800;
    font-family: 'Inter', sans-serif;
    color: #111827;
    margin: 0 0 20px 0;
    letter-spacing: -0.01em;
    text-align: center;
    
    &::after {
      content: '';
      display: block;
      width: 40px;
      height: 3px;
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      border-radius: 2px;
      margin: 8px auto 0;
    }
  }
`;

const FilterRow = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 850px) {
    margin-bottom: 12px;
  }
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
  
  @media (max-width: 850px) {
    color: #374151;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 12px;
    
    &::before {
      content: '•';
      color: #3b82f6;
      font-size: 18px;
      font-weight: 900;
    }
  }
`;

const CustomDropdownContainer = styled.div`
  position: relative;
  
  @media (max-width: 850px) {
    z-index: 1000; /* Erhöhter z-index für Mobile Container */
  }
`;

const CustomDropdownButton = styled.button<{ isOpen?: boolean }>`
  width: 100%;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid ${props => props.isOpen ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  font-size: 15px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 850px) {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid ${props => props.isOpen ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.3)'};
    color: #374151;
    padding: 16px 20px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
      border-radius: 16px;
      transition: opacity 0.3s ease;
      opacity: ${props => props.isOpen ? 1 : 0};
    }
    
    &:hover {
      border-color: rgba(59, 130, 246, 0.3);
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.85));
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
    }

    &:focus {
      border-color: rgba(59, 130, 246, 0.5);
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.9));
      outline: 2px solid rgba(59, 130, 246, 0.2);
      outline-offset: 2px;
    }

    &:active {
      transform: translateY(0);
      transition: transform 0.1s ease;
    }
  }
`;

const DropdownIconWrapper = styled.div<{ isOpen?: boolean }>`
  width: 16px;
  height: 16px;
  color: #6c757d;
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: '▼';
    font-size: 12px;
  }
`;

const CustomDropdownMenu = styled.div<{ isOpen?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  margin-top: 8px;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'};
  transition: all 0.3s ease;
  max-height: 240px;
  overflow-y: auto;
  
  @media (max-width: 850px) {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 16px;
    margin-top: 12px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04);
    max-height: 280px;
    z-index: 9999; /* Höherer z-index für Mobile um über Apartment Cards zu erscheinen */
    
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
      border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(135deg, #2563eb, #1e40af);
    }
  }
`;

const DropdownScrollIndicator = styled.div`
  display: none;
  
  @media (max-width: 850px) {
    display: block;
    position: absolute;
    bottom: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
    background: rgba(52, 152, 219, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #3498db;
    font-weight: 500;
    pointer-events: none;
    z-index: 1001;
    
    &::after {
      content: '⇅';
    }
  }
`;

const CustomDropdownOption = styled.button<{ isSelected?: boolean }>`
  width: 100%;
  padding: 12px 20px;
  background: ${props => props.isSelected ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  border: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  color: ${props => props.isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.8)'};
  font-weight: ${props => props.isSelected ? 500 : 400};
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  
  @media (max-width: 850px) {
    padding: 16px 20px;
    font-size: 15px;
    font-weight: ${props => props.isSelected ? 700 : 500};
    background: ${props => props.isSelected 
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(29, 78, 216, 0.05))' 
      : 'transparent'
    };
    color: ${props => props.isSelected ? '#1d4ed8' : '#374151'};
    border-radius: 12px;
    margin: 4px 8px;
    position: relative;
    
    ${props => props.isSelected && `
      &::before {
        content: '✓';
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: #1d4ed8;
        font-weight: 700;
        font-size: 14px;
      }
    `}
    
    &:hover {
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(29, 78, 216, 0.02));
      color: #1f2937;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    &:first-child,
    &:last-child {
      border-radius: 12px;
    }

    &:active {
      transform: translateY(0);
      transition: transform 0.1s ease;
    }
  }
`;

const ToggleContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 16px 20px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.15);
  }
  
  @media (max-width: 850px) {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 16px;
    padding: 20px 24px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 16px;
      padding: 1px;
      background: linear-gradient(145deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.1));
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: xor;
      -webkit-mask-composite: xor;
      pointer-events: none;
    }
    
    &:hover {
      border-color: rgba(59, 130, 246, 0.3);
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.85));
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
    }

    &:active {
      transform: translateY(0);
      transition: transform 0.1s ease;
    }
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 850px) {
    color: #374151;
    font-size: 16px;
    font-weight: 600;
    gap: 12px;
  }
`;

const ToggleSwitch = styled.div<{ isChecked?: boolean }>`
  position: relative;
  width: 48px;
  height: 26px;
  background: ${props => props.isChecked 
    ? 'linear-gradient(135deg, #10b981, #059669)' 
    : 'linear-gradient(135deg, #e2e8f0, #cbd5e1)'
  };
  border-radius: 13px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.isChecked 
    ? 'rgba(16, 185, 129, 0.3)' 
    : 'rgba(203, 213, 225, 0.6)'
  };

  &:before {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    background: linear-gradient(145deg, #ffffff, #f8fafc);
    border-radius: 50%;
    top: 2px;
    left: ${props => props.isChecked ? '24px' : '2px'};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(226, 232, 240, 0.6);
  }
  
  @media (max-width: 850px) {
    width: 56px;
    height: 32px;
    border-radius: 16px;
    background: ${props => props.isChecked 
      ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
      : 'linear-gradient(135deg, #e5e7eb, #d1d5db)'
    };
    box-shadow: ${props => props.isChecked 
      ? '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' 
      : '0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
    };
    border: 1px solid ${props => props.isChecked 
      ? 'rgba(59, 130, 246, 0.3)' 
      : 'rgba(209, 213, 219, 0.6)'
    };

    &:before {
      width: 28px;
      height: 28px;
      top: 2px;
      left: ${props => props.isChecked ? '26px' : '2px'};
      background: linear-gradient(145deg, #ffffff, #f8fafc);
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(229, 231, 235, 0.8);
    }
    
    &:hover {
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
      transition: transform 0.1s ease;
    }
  }
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  display: none;
`;

const ClearFiltersButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:active {
    background: rgba(255, 255, 255, 0.15);
  }
  
  @media (max-width: 850px) {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 16px;
    padding: 16px 24px;
    font-size: 16px;
    font-weight: 700;
    margin-top: 24px;
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s ease;
    }
    
    &:hover {
      background: linear-gradient(135deg, #dc2626, #b91c1c);
      transform: translateY(-1px);
      box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
      
      &::before {
        left: 100%;
      }
    }

    &:active {
      background: linear-gradient(135deg, #b91c1c, #991b1b);
      transform: translateY(0) scale(0.98);
      transition: transform 0.1s ease;
    }
  }
`;

// New UX Components for Mobile
const ResultsSummary = styled.div`
  display: none;
  
  @media (max-width: 850px) {
    display: block;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(29, 78, 216, 0.04));
    border: 1px solid rgba(59, 130, 246, 0.15);
    border-radius: 16px;
    padding: 16px 20px;
    margin-bottom: 24px;
    backdrop-filter: blur(10px);
    text-align: center;
  }
`;

const ResultsText = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
`;

const ResultsNumber = styled.span`
  font-size: 28px;
  font-weight: 800;
  color: #1d4ed8;
  font-family: 'Inter', sans-serif;
`;

const ResultsLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  font-family: 'Inter', sans-serif;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 850px) {
    gap: 20px;
  }
`;

// Selection Components für Desktop und Mobile
const SelectionContainer = styled.div`
  display: block;
`;

const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 8px;
  margin-top: 8px;
  
  @media (min-width: 851px) {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: 12px;
    margin-top: 12px;
  }
`;

const SelectionButton = styled.button<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  border: 2px solid ${props => props.isSelected ? '#3b82f6' : 'rgba(229, 231, 235, 0.8)'};
  border-radius: 12px;
  background: ${props => props.isSelected 
    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(29, 78, 216, 0.05))' 
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))'
  };
  color: ${props => props.isSelected ? '#1d4ed8' : '#6b7280'};
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 48px;
  position: relative;
  
  ${props => props.isSelected && `
    &::after {
      content: '✓';
      position: absolute;
      top: -4px;
      right: -4px;
      width: 18px;
      height: 18px;
      background: #3b82f6;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: 700;
    }
  `}
  
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: #3b82f6;
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(29, 78, 216, 0.03));
      color: #1d4ed8;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.15);
    }
  }
  
  &:active {
    transform: translateY(-1px);
    transition: transform 0.1s ease;
  }
  
  @media (min-width: 851px) {
    min-height: 52px;
    padding: 14px 12px;
    font-size: 15px;
    border: 2px solid ${props => props.isSelected ? '#3b82f6' : 'rgba(255, 255, 255, 0.3)'};
    background: ${props => props.isSelected 
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(29, 78, 216, 0.08))' 
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(248, 250, 252, 0.05))'
    };
    color: ${props => props.isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.8)'};
    backdrop-filter: blur(10px);
    
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(29, 78, 216, 0.1));
        color: #ffffff;
        border-color: rgba(255, 255, 255, 0.5);
      }
    }
  }
`;

const CustomDropdown: React.FC<{
    value: string;
    onChange: (value: string) => void;
    options: { value: string; label: string; icon?: React.ReactNode }[];
    placeholder: string;
    label: string;
}> = ({ value, onChange, options, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const selectedOption = options.find(opt => opt.value === value);

    useEffect(() => {
        if (isOpen && menuRef.current && window.innerWidth <= 768) {
            const element = menuRef.current;
            const isScrollable = element.scrollHeight > element.clientHeight;
            setShowScrollIndicator(isScrollable);
        } else {
            setShowScrollIndicator(false);
        }
    }, [isOpen, options]);

    return (
        <FilterRow>
            <FilterLabel>{label}</FilterLabel>
            <CustomDropdownContainer>
                <CustomDropdownButton
                    isOpen={isOpen}
                    onClick={() => setIsOpen(!isOpen)}
                    onBlur={() => setTimeout(() => setIsOpen(false), 150)}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {selectedOption?.icon}{selectedOption?.icon ? ' ' : ''}{selectedOption?.label || placeholder}
                    </span>
                    <DropdownIconWrapper isOpen={isOpen} />
                </CustomDropdownButton>
                <CustomDropdownMenu ref={menuRef} isOpen={isOpen}>
                    {options.map((option) => (
                        <CustomDropdownOption
                            key={option.value}
                            isSelected={option.value === value}
                            onClick={() => {
                                onChange(option.value);
                                setIsOpen(false);
                            }}
                        >
                            {option.icon}{option.icon ? ' ' : ''}{option.label}
                        </CustomDropdownOption>
                    ))}
                    {showScrollIndicator && <DropdownScrollIndicator />}
                </CustomDropdownMenu>
            </CustomDropdownContainer>
        </FilterRow>
    );
};

const CustomToggle: React.FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
    icon?: React.ReactNode;
}> = ({ checked, onChange, label, icon }) => {
    const handleToggle = () => {
        onChange(!checked);
    };

    return (
        <FilterRow>
            <ToggleContainer onClick={handleToggle}>
                <ToggleWrapper>
                    <ToggleLabel>
                        {icon}{icon ? ' ' : ''}{label}
                    </ToggleLabel>
                    <HiddenCheckbox
                        checked={checked}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                    <ToggleSwitch
                        isChecked={checked}
                    />
                </ToggleWrapper>
            </ToggleContainer>
        </FilterRow>
    );
};

// Room Selection Component
const RoomSelection: React.FC<{
    selectedRooms: string[];
    onRoomsChange: (value: string[]) => void;
    roomOptions: { value: string; label: string }[];
}> = ({ selectedRooms, onRoomsChange, roomOptions }) => {
    // Filter out the "Alle Zimmeranzahlen" option and extract room numbers
    const roomNumbers = roomOptions
        .filter(option => option.value !== '')
        .map(option => ({
            value: option.value,
            label: option.value === '1' ? '1' : option.value === '2' ? '2' : option.value === '3' ? '3' : option.value === '4' ? '4+' : option.value
        }));

    const handleRoomToggle = (roomValue: string) => {
        if (selectedRooms.includes(roomValue)) {
            // Remove from selection
            onRoomsChange(selectedRooms.filter(room => room !== roomValue));
        } else {
            // Add to selection
            onRoomsChange([...selectedRooms, roomValue]);
        }
    };

    return (
        <SelectionContainer>
            <FilterRow>
                <FilterLabel>
                    <BedDouble size={14} style={{ marginRight: '8px' }} />
                    Zimmeranzahl
                    {selectedRooms.length > 1 && (
                        <span style={{ 
                            marginLeft: 'auto', 
                            fontSize: '12px', 
                            color: '#3b82f6', 
                            fontWeight: '600',
                            background: 'rgba(59, 130, 246, 0.1)',
                            padding: '2px 8px',
                            borderRadius: '12px'
                        }}>
                            {selectedRooms.length}
                        </span>
                    )}
                </FilterLabel>
                <SelectionGrid>
                    {roomNumbers.map(room => (
                        <SelectionButton
                            type="button"
                            key={room.value}
                            isSelected={selectedRooms.includes(room.value)}
                            onClick={() => handleRoomToggle(room.value)}
                        >
                            {room.label}
                        </SelectionButton>
                    ))}
                </SelectionGrid>
            </FilterRow>
        </SelectionContainer>
    );
};

// Floor Selection Component
const FloorSelection: React.FC<{
    selectedFloor: string[];
    onFloorChange: (value: string[]) => void;
    floorOptions: { value: string; label: string }[];
}> = ({ selectedFloor, onFloorChange, floorOptions }) => {
    const [isMobile, setIsMobile] = React.useState(false);
    
    React.useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);
    
    // Filter out the "Alle Stockwerke" option and extract floor labels
    const floorNumbers = floorOptions
        .filter(option => option.value !== '')
        .map(option => ({
            value: option.value,
            // Sowohl Mobile als auch Desktop zeigen "Attika" für 3. Stock
            label: option.value === '0' ? 'EG' : 
                   option.value === '1' ? '1. OG' : 
                   option.value === '2' ? '2. OG' : 
                   option.value === '3' ? 'Attika' : 
                   `${option.value}. OG`
        }));

    const handleFloorToggle = (floorValue: string) => {
        if (selectedFloor.includes(floorValue)) {
            // Remove from selection
            onFloorChange(selectedFloor.filter(floor => floor !== floorValue));
        } else {
            // Add to selection
            onFloorChange([...selectedFloor, floorValue]);
        }
    };

    return (
        <SelectionContainer>
            <FilterRow>
                <FilterLabel>
                    <ArrowUp size={14} style={{ marginRight: '8px' }} />
                    Stockwerk
                    {selectedFloor.length > 1 && (
                        <span style={{ 
                            marginLeft: 'auto', 
                            fontSize: '12px', 
                            color: '#3b82f6', 
                            fontWeight: '600',
                            background: 'rgba(59, 130, 246, 0.1)',
                            padding: '2px 8px',
                            borderRadius: '12px'
                        }}>
                            {selectedFloor.length}
                        </span>
                    )}
                </FilterLabel>
                <SelectionGrid>
                    {floorNumbers.map(floor => (
                        <SelectionButton
                            type="button"
                            key={floor.value}
                            isSelected={selectedFloor.includes(floor.value)}
                            onClick={() => handleFloorToggle(floor.value)}
                        >
                            {floor.label}
                        </SelectionButton>
                    ))}
                </SelectionGrid>
            </FilterRow>
        </SelectionContainer>
    );
};


interface PureGlassmorphicFilterPanelProps {
    selectedFloor: string[];
    selectedRooms: string[];
    showReserved: boolean;
    floorOptions: { value: string; label: string }[];
    roomOptions: { value: string; label: string }[];
    onFloorChange: (value: string[]) => void;
    onRoomsChange: (value: string[]) => void;
    onShowReservedToggle: (checked: boolean) => void;
    onClearFilters: () => void;
    resultCount: number;
    totalCount: number;
}

const PureGlassmorphicFilterPanel: React.FC<PureGlassmorphicFilterPanelProps> = ({
    selectedFloor,
    selectedRooms,
    showReserved,
    floorOptions,
    roomOptions,
    onFloorChange,
    onRoomsChange,
    onShowReservedToggle,
    onClearFilters,
    resultCount,
    totalCount
}) => {
    const hasActiveFilters = selectedFloor.length > 0 || selectedRooms.length > 0 || showReserved;
    
    return (
        <FilterSection>
            <SectionTitle>Filter</SectionTitle>
            
            {/* Simple Results Summary */}
            <ResultsSummary>
                <ResultsText>
                    <ResultsNumber>{resultCount}</ResultsNumber>
                    <ResultsLabel> von {totalCount} Wohnungen gefunden</ResultsLabel>
                </ResultsText>
            </ResultsSummary>

            {/* Main Filters - Simple and Clean */}
            <FilterContainer>
                {/* Floor Selection Boxes - Desktop und Mobile */}
                <FloorSelection
                    selectedFloor={selectedFloor}
                    onFloorChange={onFloorChange}
                    floorOptions={floorOptions}
                />
                
                {/* Room Selection Boxes - Desktop und Mobile */}
                <RoomSelection
                    selectedRooms={selectedRooms}
                    onRoomsChange={onRoomsChange}
                    roomOptions={roomOptions}
                />
                
                <CustomToggle
                    checked={showReserved}
                    onChange={onShowReservedToggle}
                    label="Reservierte anzeigen"
                    icon={<Check size={14} />}
                />
            </FilterContainer>

            {hasActiveFilters && (
                <ClearFiltersButton onClick={onClearFilters}>
                    Filter zurücksetzen
                </ClearFiltersButton>
            )}
        </FilterSection>
    );
};

export default PureGlassmorphicFilterPanel;
